import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import VerticalScroll from '@qpr/components/ScrollBar/VerticalScroll';
import React from 'react';
import { Filter } from '../filter-state';

const dropdownHeight = '80vh';

const PREFIX = 'FilterList';

const classes = {
  filterItem: `${PREFIX}-filterItem`,
  selected: `${PREFIX}-selected`,
  icon: `${PREFIX}-icon`,
  addButton: `${PREFIX}-addButton`,
  search: `${PREFIX}-search`,
  dropdown: `${PREFIX}-dropdown`,
  filterArea: `${PREFIX}-filterArea`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.filterItem}`]: {
    '&:hover': {
      background: theme.palette.primary.light,
      cursor: 'pointer',
      '& .FilterList-icon': {
        visibility: 'visible'
      }
    }
  },

  [`& .${classes.selected}`]: {
    background: alpha(theme.palette.grey[900], 0.08)
  },

  [`& .${classes.icon}`]: {
    visibility: 'hidden',
    color: theme.palette.grey[600],
    marginLeft: theme.spacing(1)
  },

  [`& .${classes.addButton}`]: {
    padding: theme.spacing(2),
    position: 'absolute',
    bottom: '0',
    '&:hover': {
      background: theme.palette.primary.light
    }
  },

  [`& .${classes.search}`]: {
    padding: theme.spacing(1),
    backgroundColor: alpha(theme.palette.primary.main, 0.08)
  },

  [`& .${classes.dropdown}`]: {
    width: '400px',
    height: dropdownHeight
  },

  [`& .${classes.filterArea}`]: {
    maxHeight: `calc(${dropdownHeight} - 48px - 56px)`
  }
}));

interface FilterListProps {
  filters?: Filter[];
  anchorElement: Element | null;
  handleClose?: () => void;
  handleSelect?: (filterToSelect: Filter) => void;
  openEditor?: (filterToOpen: Filter | undefined) => void;
  handleDelete?: (filterToDelete: Filter) => void;
}

const FilterList = ({
  handleClose,
  handleSelect,
  openEditor,
  handleDelete,
  anchorElement = null,
  filters = [] as Filter[]
}: FilterListProps) => {
  const open = Boolean(anchorElement);

  const publicFilters = filters.filter(filter => filter.isPublic);

  const privateFilters = filters.filter(filter => !filter.isPublic && filter.owner);

  const usersFilters = filters.filter(filter => !filter.isPublic && !filter.owner);

  const handleClick = (filter: Filter) => {
    if (handleSelect) handleSelect(filter);
    if (handleClose) handleClose();
  };

  const handleDeleteClick = (filter: Filter) => (event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (handleDelete) handleDelete(filter);
    if (handleClose) handleClose();
  };

  const clickEdit =
    (filter: Filter | undefined) =>
    (event: React.MouseEvent<SVGSVGElement> | React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (openEditor) openEditor(filter);
      if (handleClose) handleClose();
    };
  const listData = (
    filterData: Filter[],
    subheader: string,
    addOwner: boolean = false,
    showIfempty: boolean = true
  ) => {
    if (filterData.length === 0 && !showIfempty) return null;
    return (
      <Root>
        <List
          subheader={
            <ListSubheader disableSticky component="div">
              <Typography variant="caption">{subheader}</Typography>
            </ListSubheader>
          }
          data-testid={`filter-list-${subheader}`}
        >
          {filterData && filterData.length > 0
            ? filterData.map((filter_: Filter) => {
                return (
                  <ListItem
                    data-testid="filter-instance"
                    className={
                      filter_.selected
                        ? `${classes.filterItem} ${classes.selected}`
                        : classes.filterItem
                    }
                    onClick={() => handleClick({ ...filter_, enabled: true })}
                    key={filter_._id}
                  >
                    <ListItemText>
                      <span title={filter_.name} style={{ float: 'left', maxWidth: '100%' }}>
                        {addOwner && (
                          <span style={{ float: 'right' }}>
                            &nbsp;({filter_.createdBy || 'unknown user'})
                          </span>
                        )}
                        <span
                          style={{
                            display: 'block',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                          }}
                        >
                          {filter_.name}
                        </span>
                      </span>
                    </ListItemText>
                    {filter_.owner || filter_.createdBy ? (
                      <>
                        <DeleteIcon
                          className={classes.icon}
                          onClick={handleDeleteClick(filter_)}
                          data-testid="delete-icon"
                        />
                        <EditIcon
                          className={classes.icon}
                          onClick={clickEdit(filter_)}
                          data-testid="edit-icon"
                        />
                      </>
                    ) : (
                      <div style={{ width: '48px', flexShrink: 0 }} />
                    )}
                  </ListItem>
                );
              })
            : null}
        </List>
        <Divider />
      </Root>
    );
  };

  return (
    <Popover
      keepMounted
      open={open}
      anchorEl={anchorElement}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
    >
      <Root>
        <Paper className={classes.dropdown}>
          <FormControl fullWidth className={classes.search}>
            <Input
              disableUnderline
              placeholder="Search"
              id="input-with-icon-adornment"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
            />
          </FormControl>
          <VerticalScroll>
            <div className={classes.filterArea} data-testid="filter-area">
              {listData(publicFilters, 'Public Filters')}
              {listData(privateFilters, 'My Filters')}
              {listData(usersFilters, "Users' Filters", true, false)}
            </div>
          </VerticalScroll>
          <Button
            fullWidth
            color="primary"
            onClick={clickEdit(undefined)}
            startIcon={<AddIcon />}
            className={classes.addButton}
            data-testid="add-filter-button"
          >
            Add Filter
          </Button>
        </Paper>
      </Root>
    </Popover>
  );
};

export default FilterList;
